import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface FormData {
  name: string;
  email: string;
  interest: string;
}

interface WidgetSettings {
  school_name?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  logo_url?: string;
}

const INTEREST_OPTIONS = [
  { value: '', label: 'Select your interest...' },
  { value: 'adhd', label: 'My child has ADHD' },
  { value: 'autism', label: 'My child has autism' },
  { value: 'learning', label: 'My child struggles with learning differences' },
  { value: 'researching', label: 'Just researching options' }
];

const NewsletterForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);
  const [resourceName, setResourceName] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [settings, setSettings] = useState<WidgetSettings>({
    school_name: 'My Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    interest: ''
  });

  // Fetch widget settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/config`);
        if (response.ok) {
          const data = await response.json();
          setSettings({
            school_name: data.school_name || 'My Academy',
            primary_color: data.primary_color || '#4F46E5',
            secondary_color: data.secondary_color || '#7C3AED',
            accent_color: data.accent_color || '#10B981',
            logo_url: data.logo_url
          });
        }
      } catch (error) {
        console.error('Failed to fetch widget settings:', error);
      } finally {
        setFetchingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  // Fetch newsletter resource name on mount
  useEffect(() => {
    const fetchResourceName = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/newsletter-resource`);
        if (response.ok) {
          const data = await response.json();
          setResourceName(data.resourceTitle);
        }
      } catch (error) {
        console.error('Failed to fetch newsletter resource:', error);
        // Silently fail - resource name is optional
      }
    };
    fetchResourceName();
  }, []);

  // Validation helpers
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const submitData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        interest: formData.interest || null
      };

      console.log('Submitting newsletter signup:', submitData);

      const response = await fetch(`${API_URL}/api/public/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      console.log('Newsletter signup successful:', result);

      // Navigate to success page with download link and type parameter for GA4 tracking
      navigate('/contact/success?type=newsletter', {
        state: {
          email: formData.email,
          resources: result.resources || [],
          message: 'Thank you for subscribing! Download your free resource below.'
        }
      });

    } catch (error: any) {
      console.error('Newsletter signup error:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while fetching settings
  if (fetchingSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-t-4" style={{ borderColor: settings.primary_color }}></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          {settings.logo_url && (
            <div className="mb-6 flex justify-center">
              <img
                src={settings.logo_url}
                alt={`${settings.school_name} Logo`}
                className="h-24 w-24 object-contain"
              />
            </div>
          )}

          <div className="mb-2">
            <p className="text-sm uppercase tracking-wide text-gray-600 mb-2">Sign up to receive:</p>
            <h1 className="text-4xl font-bold mb-2" style={{ color: settings.primary_color || '#4F46E5' }}>
              {resourceName || 'Free Educational Resources'}
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Subscribe to our newsletter and get instant access
          </p>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 space-y-6">

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="error-message mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition`}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="error-message mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Interest Dropdown (Optional) */}
            <div>
              <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-1">
                What brings you here? (optional)
              </label>
              <select
                id="interest"
                value={formData.interest}
                onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition bg-white"
              >
                {INTEREST_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Newsletter Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    📬 You'll be subscribed to our newsletter
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Get helpful resources and updates delivered to your inbox
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 rounded-lg font-semibold text-lg transition shadow-lg text-white"
              style={{
                backgroundColor: loading ? '#9CA3AF' : settings.primary_color,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!loading && settings.secondary_color) {
                  e.currentTarget.style.backgroundColor = settings.secondary_color;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && settings.primary_color) {
                  e.currentTarget.style.backgroundColor = settings.primary_color;
                }
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                'Get My Free Resource'
              )}
            </button>

            {/* Privacy Notice */}
            <p className="text-center text-sm text-gray-500 mt-4">
              🔒 Your information is secure and will never be shared. Unsubscribe anytime.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsletterForm;
